import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, orderBy, where, deleteDoc, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function AdminShipmentsNew() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState('');
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [newEvent, setNewEvent] = useState({
    status: 'in_transit',
    location: '',
    description: '',
    timestamp: new Date().toISOString().slice(0, 16)
  });

  useEffect(() => {
    fetchShipments();
  }, [filter]);

  async function fetchShipments() {
    try {
      let shipmentsQuery;
      
      if (filter === 'all') {
        shipmentsQuery = query(
          collection(db, 'shipments'),
          orderBy('createdAt', 'desc')
        );
      } else {
        shipmentsQuery = query(
          collection(db, 'shipments'),
          where('status', '==', filter),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(shipmentsQuery);
      const shipmentList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShipments(shipmentList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      setLoading(false);
      setMessage({ type: 'error', text: 'Failed to fetch shipments' });
    }
  }

  async function handleDelete(shipmentId) {
    if (window.confirm('Are you sure you want to delete this shipment?')) {
      try {
        await deleteDoc(doc(db, 'shipments', shipmentId));
        setShipments(shipments.filter(s => s.id !== shipmentId));
        setMessage({ type: 'success', text: 'Shipment deleted successfully' });
      } catch (error) {
        console.error('Error deleting shipment:', error);
        setMessage({ type: 'error', text: 'Failed to delete shipment' });
      }
    }
  }

  const handleEditLocation = (shipment) => {
    setEditingLocation(shipment.id);
    setNewLocation(shipment.currentLocation || '');
  };

  const handleSaveLocation = async (shipmentId) => {
    try {
      const shipmentRef = doc(db, 'shipments', shipmentId);
      await updateDoc(shipmentRef, {
        currentLocation: newLocation,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setShipments(shipments.map(shipment => 
        shipment.id === shipmentId 
          ? { ...shipment, currentLocation: newLocation }
          : shipment
      ));

      setEditingLocation(null);
      setMessage({ type: 'success', text: 'Location updated successfully' });
    } catch (error) {
      console.error('Error updating location:', error);
      setMessage({ type: 'error', text: 'Failed to update location' });
    }
  };

  const handleCancelEdit = () => {
    setEditingLocation(null);
    setNewLocation('');
  };

  const handleAddTrackingEvent = async (shipmentId) => {
    try {
      const shipmentRef = doc(db, 'shipments', shipmentId);
      const event = {
        ...newEvent,
        timestamp: new Date(newEvent.timestamp).toISOString()
      };

      await updateDoc(shipmentRef, {
        timeline: arrayUnion(event),
        status: newEvent.status,
        currentLocation: newEvent.location,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setShipments(shipments.map(shipment => {
        if (shipment.id === shipmentId) {
          const updatedTimeline = [...(shipment.timeline || []), event].sort((a, b) => 
            new Date(b.timestamp) - new Date(a.timestamp)
          );
          return {
            ...shipment,
            status: newEvent.status,
            currentLocation: newEvent.location,
            timeline: updatedTimeline
          };
        }
        return shipment;
      }));

      setShowAddEventModal(false);
      setNewEvent({
        status: 'in_transit',
        location: '',
        description: '',
        timestamp: new Date().toISOString().slice(0, 16)
      });
      setMessage({ type: 'success', text: 'Tracking event added successfully' });
    } catch (error) {
      console.error('Error adding tracking event:', error);
      setMessage({ type: 'error', text: 'Failed to add tracking event' });
    }
  };

  const handleOpenAddEventModal = (shipment) => {
    setSelectedShipment(shipment);
    setNewEvent({
      status: shipment.status,
      location: shipment.currentLocation || '',
      description: '',
      timestamp: new Date().toISOString().slice(0, 16)
    });
    setShowAddEventModal(true);
  };

  const filteredShipments = shipments.filter(shipment => 
    shipment.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shipment.recipientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shipment.recipientEmail?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-white">Manage Shipments</h1>
            <button
              onClick={() => navigate('/admin/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              New Shipment
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by tracking number, recipient name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 rounded-lg bg-slate-800 border border-white/10 text-white focus:outline-none focus:border-blue-500 [&>option]:bg-slate-800 [&>option]:text-white"
            >
              <option value="all">All Shipments</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="in_transit">In Transit</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="exception">Exception</option>
            </select>
          </div>
        </div>

        {/* Shipments Table */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/20">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Tracking Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Current Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/20">
                {filteredShipments.map((shipment) => (
                  <tr key={shipment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {shipment.trackingNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      <div>{shipment.recipientName}</div>
                      <div className="text-gray-400">{shipment.recipientEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        shipment.status === 'delivered' 
                          ? 'bg-green-500/20 text-green-300'
                          : shipment.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {shipment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {editingLocation === shipment.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newLocation}
                            onChange={(e) => setNewLocation(e.target.value)}
                            className="p-2 bg-white/5 border border-white/20 rounded text-white"
                            placeholder="Enter new location"
                          />
                          <button
                            onClick={() => handleSaveLocation(shipment.id)}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className="text-white">{shipment.currentLocation || 'Not set'}</span>
                          <button
                            onClick={() => handleEditLocation(shipment)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {new Date(shipment.createdAt?.toDate()).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenAddEventModal(shipment)}
                          className="text-green-400 hover:text-green-300"
                        >
                          Add Event
                        </button>
                        <button
                          onClick={() => navigate(`/admin/edit/${shipment.id}`)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(shipment.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      {showAddEventModal && selectedShipment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Add Tracking Event</h3>
              <button 
                onClick={() => setShowAddEventModal(false)}
                className="text-white/60 hover:text-white"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleAddTrackingEvent(selectedShipment.id);
            }} className="space-y-4">
              <div>
                <label className="block text-white/80 mb-2">Status</label>
                <select
                  value={newEvent.status}
                  onChange={(e) => setNewEvent({ ...newEvent, status: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-white/10 text-white focus:outline-none focus:border-blue-500 [&>option]:bg-slate-800 [&>option]:text-white"
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="in_transit">In Transit</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="exception">Exception</option>
                </select>
              </div>

              <div>
                <label className="block text-white/80 mb-2">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:border-blue-500"
                  placeholder="Enter location"
                />
              </div>

              <div>
                <label className="block text-white/80 mb-2">Date and Time</label>
                <input
                  type="datetime-local"
                  value={newEvent.timestamp}
                  onChange={(e) => setNewEvent({ ...newEvent, timestamp: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-white/10 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-white/80 mb-2">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:border-blue-500"
                  placeholder="Enter event description"
                  rows="3"
                />
              </div>

              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300"
              >
                Add Event
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 